import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { adoptionProcessResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { ON_PLATFORM_POLICY_PATH } from 'dpl/shared/constants/urls';
import ClampedMultiLineText from 'dpl/common/components/ClampedMultiLineText';
import Banner from 'dpl/common/design_system/Banner';
import BreederProfileAdoptionProcessSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileAdoptionProcessSkeleton';
import SmartLink from 'dpl/components/SmartLink';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';
import SmartImage from 'dpl/components/SmartImage';
import { WhatsIncludedSection } from 'dpl/components/BreederProfile/mobile/components/shared/WhatsIncluded';
import useIncludedItems from 'dpl/components/BreederProfile/mobile/hooks/useIncludedItems';
import useToggle from 'dpl/shared/hooks/useToggle';
import PriceBreakdownModal from 'dpl/common/components/PriceBreakdownModal';
import BuyNowPayLaterDetails from 'dpl/puppy_page/components/BuyNowPayLaterDetails';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';

import spottedPuppySketchPng from 'dpl/assets/spotted_puppy_sketch.png';

const IS_LT_MD = isLessThanBreakpoint('md');
const CLAMPED_TEXT_LINE_HEIGHT = IS_LT_MD ? 22.4 : 25.2;

const DEFAULT_MATCHING_POLICY_COPY =
  'Together, you’ll choose the puppy that’s right for you, stay in touch with regular updates, and plan how to bring your new puppy home.';

export default function BreederProfileAdoptionProcessSectionContainer() {
  const includedItems = useIncludedItems();

  const itemColumns = useMemo(() => {
    const midIndex = Math.ceil(includedItems.length / 2);

    return includedItems.reduce(
      (acc, item, index) => {
        const key = IS_LT_MD || midIndex > index ? 0 : 1;
        acc[key].push(item);
        return acc;
      },
      IS_LT_MD ? [[]] : [[], []]
    );
  }, [includedItems]);

  const {
    breederFirstName,
    breederProfileId,
    analyticsPageName,
    priceBreakdown,
    acceptsBnplPayments,
    depositRefundPolicy,
    depositDescription,
    includedExtrasParagraphs,
    puppyMatchingPolicyDescription,
    matchingPolicyDescription
  } = useSelector(({ server }) => server);

  const {
    isFetching,
    value: { data: adoptionProcess }
  } = useResourceQuery(adoptionProcessResourceDefinition(breederProfileId));

  const [isBreakdownVisible, toggleIsBreakdownVisible] = useToggle(false);

  function handlePriceBreakdownClick(e) {
    toggleIsBreakdownVisible();

    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: e.target.textContent,
      view: ANALYTICS_VIEWS.BREEDER_PROFILE,
      view_feature: ANALYTICS_VIEW_FEATURES.PRICE_BREAKDOWN_BUTTON,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.PRICING_SECTION
    });
  }

  if (isFetching) {
    return <BreederProfileAdoptionProcessSkeleton />;
  }

  return (
    <SectionViewTracker
      name={analyticsPageName}
      category="breeder-profile-about-program"
      properties={{ breeder_profile_id: breederProfileId }}
      rootMargin="-300px 0px"
      threshold={0}
    >
      <section className="BreederProfileAdoptionProcessSection bg-sand pv12 pv20-md font-16 md:font-18">
        <div className="container-xxl">
          <div className="flex flex-column gap-32 md:gap-96">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-32 md:gap-96">
              <div>
                <h3 className="font-24 md:font-40 lh-title mb6 mb8-md">
                  Getting a puppy from {breederFirstName}
                </h3>
                <div className="font-16 md:font-18 secondary lh-body">
                  <p className="mb2 mb3-md">
                    {breederFirstName} has been certified by Good Dog’s
                    screening team for responsible and trusted breeding
                    practices. When you’re ready to reach out, feel free to ask
                    any questions about the breed, their program, or specific
                    puppies.
                  </p>
                  <p>
                    {puppyMatchingPolicyDescription ||
                      DEFAULT_MATCHING_POLICY_COPY}
                  </p>
                  {matchingPolicyDescription && (
                    <ClampedMultiLineText
                      className="secondary lh-body i mt6 mt4-md"
                      text={`“${matchingPolicyDescription}”`}
                      numOfLines={IS_LT_MD ? 5 : 3}
                      lineHeight={CLAMPED_TEXT_LINE_HEIGHT}
                      moreText="Read more"
                      lessText="Show less"
                      moreButtonClassName="stone-700"
                      fadeToBackgroundColor="#FAF7F2"
                      isOpen={window.__DPL_BOT_UA}
                      fadeToButton
                    />
                  )}
                </div>
              </div>
              <SmartImage
                src={spottedPuppySketchPng}
                width={IS_LT_MD ? '140px' : '192px'}
              />
              <div>
                <h4 className="font-24 md:font-32 lh-title mb6 mb8-md">
                  Price
                </h4>
                <div className="secondary mb4">
                  <span>
                    {adoptionProcess.formatted_price_deposit_description}
                  </span>{' '}
                  {priceBreakdown && (
                    <>
                      <button
                        type="button"
                        className="fw-medium stone-700 underline db-ns mt4-md"
                        onClick={handlePriceBreakdownClick}
                      >
                        Price breakdown
                      </button>
                      <PriceBreakdownModal
                        deposit={priceBreakdown.deposit}
                        depositDescription={depositDescription}
                        depositRefundPolicy={depositRefundPolicy}
                        dogPayment={priceBreakdown.dog_payment}
                        subtotal={priceBreakdown.subtotal}
                        includedExtrasParagraphs={includedExtrasParagraphs}
                        isBnplAvailable={acceptsBnplPayments}
                        isOpen={isBreakdownVisible}
                        onClose={toggleIsBreakdownVisible}
                      />
                      {acceptsBnplPayments && (
                        <div className="mt4">
                          Pay over time with Klarna.{' '}
                          <BuyNowPayLaterDetails className="fw-medium stone-700" />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {adoptionProcess.deposit_and_price_description && (
                  <ClampedMultiLineText
                    className="secondary lh-body i"
                    text={`“${adoptionProcess.deposit_and_price_description}”`}
                    numOfLines={4}
                    lineHeight={CLAMPED_TEXT_LINE_HEIGHT}
                    moreText="Read more"
                    lessText="Show less"
                    moreButtonClassName="stone-700"
                    fadeToBackgroundColor="#FAF7F2"
                    isOpen={window.__DPL_BOT_UA}
                    fadeToButton
                  />
                )}
              </div>
              <div>
                <h4 className="font-24 md:font-32 lh-title mb6 mb8-md">
                  Contract & health guarantee
                </h4>
                <ClampedMultiLineText
                  className="secondary lh-body"
                  text={`${breederFirstName} may provide a written contract or 
                  health guarantee when you purchase a puppy. This helps 
                  protect both you and your breeder, ensuring that you 
                  both have a clear understanding of the terms of your 
                  puppy purchase. If ${breederFirstName} offers a contract 
                  or guarantee, the details will be personalized by them.  
                  If you have any questions or want to know more, 
                  don’t hesitate to reach out to ${breederFirstName} directly.`}
                  numOfLines={3}
                  lineHeight={CLAMPED_TEXT_LINE_HEIGHT}
                  moreText="Read more"
                  lessText="Show less"
                  moreButtonClassName="stone-700"
                  fadeToBackgroundColor="#FAF7F2"
                  isOpen={window.__DPL_BOT_UA}
                  fadeToButton
                />
                <Banner
                  className="items-center mt6 mt8-md"
                  color="white"
                  iconVariant="dark"
                  iconName="fetch-payment-secure"
                >
                  Always communicate and pay on Good Dog.
                  <SmartLink
                    className="stone-700 underline ml2"
                    to={ON_PLATFORM_POLICY_PATH}
                    target="_blank"
                  >
                    Learn more
                  </SmartLink>
                </Banner>
              </div>
            </div>
            <div>
              <h4 className="font-24 md:font-32 lh-title mb6 mb8-md">
                What’s included
              </h4>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-96">
                {itemColumns.map((column, colIndex) => (
                  <li key={colIndex}>
                    <ul className="flex flex-column gap-32 md:gap-56">
                      {column.map(({ id, title, items, iconName }) => (
                        <li key={id}>
                          <WhatsIncludedSection
                            key={id}
                            id={id}
                            title={title}
                            items={items}
                            iconName={iconName}
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </SectionViewTracker>
  );
}
